import { useState, useEffect } from 'react';
import { 
  generateMockFeeWorksheet, 
  pullFeesFromLOS, 
  saveFeeWorksheet,
  calculateCashToClose,
  calculateBrokerCompensation,
  addFee,
  updateFee,
  removeFee,
  getFeeWarnings,
  FEE_CATEGORIES,
  FEE_PAYER,
  FEE_SOURCE
} from '../../services/feesService';

const FeesWorksheetComponent = ({ loan }) => {
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pullingFees, setPullingFees] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [showAddFeeModal, setShowAddFeeModal] = useState(false);
  const [newFeeSection, setNewFeeSection] = useState('');
  const [cashToClose, setCashToClose] = useState(null);
  const [brokerComp, setBrokerComp] = useState(null);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    // Initialize with mock data
    const initialWorksheet = generateMockFeeWorksheet(loan);
    setWorksheet(initialWorksheet);
    updateCalculations(initialWorksheet);
  }, [loan]);

  const updateCalculations = (ws) => {
    const ctc = calculateCashToClose(ws);
    const comp = calculateBrokerCompensation(ws);
    const warns = getFeeWarnings(ws);
    
    setCashToClose(ctc);
    setBrokerComp(comp);
    setWarnings(warns);
  };

  const handlePullFromLOS = async () => {
    setPullingFees(true);
    try {
      const losWorksheet = await pullFeesFromLOS(loan.id);
      setWorksheet(losWorksheet);
      updateCalculations(losWorksheet);
      alert('Fees pulled from LOS successfully!');
    } catch (error) {
      alert('Failed to pull fees from LOS');
    } finally {
      setPullingFees(false);
    }
  };

  const handleSaveWorksheet = async () => {
    setLoading(true);
    try {
      const saved = await saveFeeWorksheet(loan.id, worksheet);
      setWorksheet(saved);
      alert('Fee worksheet saved successfully!');
    } catch (error) {
      alert('Failed to save fee worksheet');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFee = (section, feeId, field, value) => {
    const updated = updateFee(worksheet, section, feeId, { [field]: value });
    setWorksheet(updated);
    updateCalculations(updated);
  };

  const handleRemoveFee = (section, feeId) => {
    if (confirm('Are you sure you want to remove this fee?')) {
      const updated = removeFee(worksheet, section, feeId);
      setWorksheet(updated);
      updateCalculations(updated);
    }
  };

  const handleAddFee = (section, newFee) => {
    try {
      const updated = addFee(worksheet, section, newFee);
      setWorksheet(updated);
      updateCalculations(updated);
      setShowAddFeeModal(false);
    } catch (error) {
      alert(error.message);
    }
  };

  const renderFeeRow = (fee, section) => {
    const isEditing = editingFee === fee.id;
    const isReadOnly = !fee.editable;

    return (
      <tr key={fee.id} className={fee.source === FEE_SOURCE.LOS ? 'bg-blue-lt' : ''}>
        <td>
          <div className="d-flex align-items-center">
            <div>
              <div className="fw-bold">{fee.name}</div>
              <div className="text-secondary small">{fee.description}</div>
            </div>
            {fee.source === FEE_SOURCE.LOS && (
              <span className="badge bg-blue-lt ms-2">LOS</span>
            )}
            {fee.source === FEE_SOURCE.COMP_PLAN && (
              <span className="badge bg-purple-lt ms-2">Comp Plan</span>
            )}
          </div>
        </td>
        <td className="text-end" style={{ width: '150px' }}>
          {isEditing && fee.editable ? (
            <input
              type="number"
              className="form-control form-control-sm"
              value={fee.amount}
              onChange={(e) => handleUpdateFee(section, fee.id, 'amount', parseFloat(e.target.value))}
              step="0.01"
            />
          ) : (
            <span className="fw-bold">${fee.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          )}
        </td>
        <td style={{ width: '120px' }}>
          {isEditing && fee.editable ? (
            <select
              className="form-select form-select-sm"
              value={fee.payer}
              onChange={(e) => handleUpdateFee(section, fee.id, 'payer', e.target.value)}
            >
              <option value={FEE_PAYER.BORROWER}>Borrower</option>
              <option value={FEE_PAYER.LENDER}>Lender</option>
              <option value={FEE_PAYER.SELLER}>Seller</option>
            </select>
          ) : (
            <span className={`badge ${
              fee.payer === FEE_PAYER.BORROWER ? 'bg-red-lt' :
              fee.payer === FEE_PAYER.LENDER ? 'bg-green-lt' :
              'bg-yellow-lt'
            }`}>
              {fee.payer}
            </span>
          )}
        </td>
        <td className="text-end" style={{ width: '100px' }}>
          {fee.editable && (
            <div className="btn-list">
              {isEditing ? (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => setEditingFee(null)}
                >
                  <i className="ti ti-check"></i>
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-ghost-primary"
                  onClick={() => setEditingFee(fee.id)}
                >
                  <i className="ti ti-edit"></i>
                </button>
              )}
              <button
                className="btn btn-sm btn-ghost-danger"
                onClick={() => handleRemoveFee(section, fee.id)}
              >
                <i className="ti ti-trash"></i>
              </button>
            </div>
          )}
          {!fee.editable && (
            <span className="text-secondary small">Read-only</span>
          )}
        </td>
      </tr>
    );
  };

  const renderFeeSection = (title, sectionKey, fees) => {
    const sectionTotal = fees.reduce((sum, fee) => sum + fee.amount, 0);
    const borrowerTotal = fees.filter(f => f.payer === FEE_PAYER.BORROWER).reduce((sum, fee) => sum + fee.amount, 0);

    return (
      <div className="card mb-3">
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
          <div className="card-actions">
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                setNewFeeSection(sectionKey);
                setShowAddFeeModal(true);
              }}
            >
              <i className="ti ti-plus me-1"></i>
              Add Fee
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Fee Description</th>
                <th className="text-end">Amount</th>
                <th>Paid By</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fees.map(fee => renderFeeRow(fee, sectionKey))}
              <tr className="table-active">
                <td className="fw-bold">Subtotal</td>
                <td className="text-end fw-bold">${sectionTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td colSpan="2" className="text-secondary small">
                  Borrower pays: ${borrowerTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!worksheet) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary mb-3"></div>
          <div>Loading fee worksheet...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Actions */}
      <div className="card mb-3">
        <div className="card-header">
          <h3 className="card-title">
            <i className="ti ti-receipt me-2"></i>
            Fees & Loan Estimate
          </h3>
          <div className="card-actions">
            <button
              className="btn btn-sm btn-success me-2"
              onClick={handlePullFromLOS}
              disabled={pullingFees}
            >
              {pullingFees ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Pulling...
                </>
              ) : (
                <>
                  <i className="ti ti-refresh me-1"></i>
                  Pull Fees from LOS
                </>
              )}
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSaveWorksheet}
              disabled={loading}
            >
              <i className="ti ti-device-floppy me-1"></i>
              Save Worksheet
            </button>
          </div>
        </div>
        {worksheet.source === 'LOS' && (
          <div className="card-body">
            <div className="alert alert-info mb-0">
              <i className="ti ti-info-circle me-2"></i>
              Fees pulled from {worksheet.losSystem} on {new Date(worksheet.pulledAt).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="card mb-3">
          <div className="card-header bg-warning-lt">
            <h3 className="card-title">
              <i className="ti ti-alert-triangle me-2"></i>
              Warnings ({warnings.length})
            </h3>
          </div>
          <div className="card-body">
            {warnings.map((warning, idx) => (
              <div key={idx} className="alert alert-warning mb-2">
                {warning.message}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="row">
        {/* Left Column: Fee Sections */}
        <div className="col-lg-8">
          {/* A. Origination Charges */}
          {renderFeeSection('A. Origination Charges', 'origination', worksheet.fees.origination)}

          {/* B. Services Borrower Cannot Shop For */}
          {renderFeeSection('B. Services Borrower Cannot Shop For', 'cannotShop', worksheet.fees.cannotShop)}

          {/* C. Services Borrower Can Shop For */}
          {renderFeeSection('C. Services Borrower Can Shop For', 'canShop', worksheet.fees.canShop)}

          {/* D. Taxes and Government Fees */}
          {renderFeeSection('D. Taxes and Government Fees', 'taxesGov', worksheet.fees.taxesGov)}

          {/* E. Prepaids */}
          {renderFeeSection('E. Prepaids', 'prepaids', worksheet.fees.prepaids)}

          {/* F. Initial Escrow Payment at Closing */}
          {renderFeeSection('F. Initial Escrow Payment at Closing', 'escrow', worksheet.fees.escrow)}

          {/* G. Other */}
          {renderFeeSection('G. Other', 'other', worksheet.fees.other)}
        </div>

        {/* Right Column: Summary Panels */}
        <div className="col-lg-4">
          {/* Cash to Close Summary */}
          {cashToClose && (
            <div className="card mb-3 sticky-top" style={{ top: '1rem' }}>
              <div className="card-header bg-primary text-white">
                <h3 className="card-title text-white">
                  <i className="ti ti-cash me-2"></i>
                  Cash to Close
                </h3>
              </div>
              <div className="card-body">
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <td>Down Payment</td>
                      <td className="text-end fw-bold">
                        ${cashToClose.downPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                    <tr>
                      <td>Closing Costs</td>
                      <td className="text-end fw-bold">
                        ${cashToClose.closingCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                    {cashToClose.lenderCredits > 0 && (
                      <tr>
                        <td>Lender Credits</td>
                        <td className="text-end text-success fw-bold">
                          -${cashToClose.lenderCredits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                    {cashToClose.sellerCredits > 0 && (
                      <tr>
                        <td>Seller Credits</td>
                        <td className="text-end text-success fw-bold">
                          -${cashToClose.sellerCredits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                    <tr className="table-active">
                      <td className="fw-bold" style={{ fontSize: '1.1rem' }}>CASH TO CLOSE</td>
                      <td className="text-end fw-bold" style={{ fontSize: '1.3rem', color: 'var(--tblr-primary)' }}>
                        ${cashToClose.cashToClose.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="card-footer">
                <div className="text-secondary small">
                  <i className="ti ti-info-circle me-1"></i>
                  Amount borrower brings to closing
                </div>
              </div>
            </div>
          )}

          {/* Loan Officer Compensation Summary */}
          {brokerComp && (
            <div className="card mb-3">
              <div className="card-header bg-purple-lt">
                <h3 className="card-title">
                  <i className="ti ti-currency-dollar me-2"></i>
                  Loan Officer Compensation
                </h3>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div className="h2 mb-0">
                    ${brokerComp.totalCompensation.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-secondary small">Total Compensation</div>
                </div>

                <table className="table table-sm mb-3">
                  <tbody>
                    {brokerComp.paidByLender > 0 && (
                      <tr>
                        <td>Paid by Lender</td>
                        <td className="text-end fw-bold">
                          ${brokerComp.paidByLender.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                    {brokerComp.paidByBorrower > 0 && (
                      <tr>
                        <td>Paid by Borrower</td>
                        <td className="text-end fw-bold">
                          ${brokerComp.paidByBorrower.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="alert alert-info mb-0">
                  <div className="small">
                    <strong>Calculation:</strong><br />
                    {brokerComp.calculation}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loan Summary */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Loan Summary</h3>
            </div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td>Loan Amount</td>
                    <td className="text-end fw-bold">
                      ${worksheet.loanAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr>
                    <td>Property Value</td>
                    <td className="text-end fw-bold">
                      ${worksheet.propertyValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr>
                    <td>Down Payment</td>
                    <td className="text-end fw-bold">
                      ${worksheet.downPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr>
                    <td>LTV</td>
                    <td className="text-end fw-bold">
                      {((worksheet.loanAmount / worksheet.propertyValue) * 100).toFixed(2)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Fee Modal */}
      {showAddFeeModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Fee</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddFeeModal(false)}></button>
              </div>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleAddFee(newFeeSection, {
                  name: formData.get('name'),
                  amount: parseFloat(formData.get('amount')),
                  payer: formData.get('payer'),
                  description: formData.get('description'),
                  category: formData.get('category'),
                  editable: true
                });
              }}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Fee Name</label>
                    <input type="text" name="name" className="form-control" required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Fee Category</label>
                    <select name="category" className="form-select" required>
                      <option value="">Select a category...</option>
                      <option value={FEE_CATEGORIES.ORIGINATION}>{FEE_CATEGORIES.ORIGINATION}</option>
                      <option value={FEE_CATEGORIES.CANNOT_SHOP}>{FEE_CATEGORIES.CANNOT_SHOP}</option>
                      <option value={FEE_CATEGORIES.CAN_SHOP}>{FEE_CATEGORIES.CAN_SHOP}</option>
                      <option value={FEE_CATEGORIES.TAXES_GOV}>{FEE_CATEGORIES.TAXES_GOV}</option>
                      <option value={FEE_CATEGORIES.PREPAIDS}>{FEE_CATEGORIES.PREPAIDS}</option>
                      <option value={FEE_CATEGORIES.ESCROW}>{FEE_CATEGORIES.ESCROW}</option>
                      <option value={FEE_CATEGORIES.OTHER}>{FEE_CATEGORIES.OTHER}</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="number" name="amount" className="form-control" step="0.01" required />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Paid By</label>
                    <select name="payer" className="form-select" required>
                      <option value={FEE_PAYER.BORROWER}>Borrower</option>
                      <option value={FEE_PAYER.LENDER}>Lender</option>
                      <option value={FEE_PAYER.SELLER}>Seller</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea name="description" className="form-control" rows="2"></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={() => setShowAddFeeModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="ti ti-plus me-1"></i>
                    Add Fee
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesWorksheetComponent;
